import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import NotificationsTray from '@edx/frontend-plugin-notifications';
import { Icon } from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}
let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0D0D0E;
            color: #ccc;
          }
          a { color: #ccc; }
          a:hover { color: #d3d3d3; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();
  const baseUrl = config.LMS_BASE_URL;
  const indigoFooterNavLinks = config.INDIGO_FOOTER_NAV_LINKS || [];
  const authenticatedUser = getAuthenticatedUser();
  const isStaffUser = Boolean(authenticatedUser && authenticatedUser.administrator);
  const messages = {
    "footer.tutorlogo.altText": {
      id: "footer.tutorlogo.altText",
      defaultMessage: "Runs on Tutor",
      description: "alt text for the footer tutor logo",
    },
    "footer.techsoup.altText": {
      id: "footer.techsoup.altText",
      defaultMessage: "TechSoup",
      description: "alt text for the footer TechSoup logo",
    },
    "footer.logo.altText": {
      id: "footer.logo.altText",
      defaultMessage: "Powered by Open edX",
      description: "alt text for the footer logo.",
    },
  };
  return (
    <div className="wrapper wrapper-footer">
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <nav className="nav-colophon" aria-label="About">
            <ol>
              {indigoFooterNavLinks.map((link, index) => (
                ((link.title !== 'Suport' || isStaffUser) && (
                <li key={`${link.url}-${index}`}>
                  <a href={link.url.startsWith("http") ? link.url : `${config.LMS_BASE_URL}${link.url}`}>
                    {link.title}
                  </a>
                </li>
                ))
              ))}
            </ol>
          </nav>
        </div>
        <div className="footer-holder">
          <p className="footer-support">
            <a className="open-edx-link" target="_blank" rel="noreferrer" href="https://open.edx.org/">
              <img
                src={`${baseUrl}/theming/asset/images/openedx-logo.png`}
                alt={intl.formatMessage(messages["footer.logo.altText"])}
                width="80"
              />
            </a>
            <a className="abstract-link" target="_blank" rel="nofollow noreferrer" href="https://abstract-technology.de/">
              by ABSTRACT-TECHNOLOGY
            </a>
          </p>
          <ul className="footer-social-list">
            <li className="footer-social-item">
              <a href="https://www.facebook.com/asociatiatechsoup" target="_blank" rel="noreferrer" aria-label="Facebook">
                <svg className="footer-social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z" /></svg>
              </a>
            </li>
            <li className="footer-social-item">
              <a href="https://www.instagram.com/asociatiatechsoup/" target="_blank" rel="noreferrer" aria-label="Instagram">
                <svg className="footer-social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" /></svg>
              </a>
            </li>
            <li className="footer-social-item">
              <a href="https://www.linkedin.com/company/asociatiatechsoup/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <svg className="footer-social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M100.3 448H7.4V148.9h92.9zM53.8 108.1C24.1 108.1 0 83.5 0 53.8a53.8 53.8 0 0 1 107.6 0c0 29.7-24.1 54.3-53.8 54.3zM447.9 448h-92.7V302.4c0-34.7-.7-79.2-48.3-79.2-48.3 0-55.7 37.7-55.7 76.7V448h-92.8V148.9h89.1v40.8h1.3c12.4-23.5 42.7-48.3 87.9-48.3 94 0 111.3 61.9 111.3 142.3V448z" /></svg>
              </a>
            </li>
            <li className="footer-social-item">
              <a href="https://www.youtube.com/techsoupromania" target="_blank" rel="noreferrer" aria-label="YouTube">
                <svg className="footer-social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6-11.4 42.9-11.4 132.3-11.4 132.3s0 89.4 11.4 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8 11.4-42.9 11.4-132.3 11.4-132.3s0-89.4-11.4-132.3zm-317.5 213.5V175.2l142.7 81.2-142.7 81.2z" /></svg>
              </a>
            </li>
          </ul>
          <div className="footer-logo">
            <a className="partner-logo techsoup-logo" href="https://www.asociatiatechsoup.ro/" target="_blank" rel="noreferrer">
              <img
                src={`${baseUrl}/theming/asset/images/logo-techsoup.svg`}
                alt={intl.formatMessage(messages["footer.techsoup.altText"])}
                height="40"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};const LearningHeaderActionsSingleTray = () => (
  <>
    <NotificationsTray />
    <a className="text-gray-700" href={`${getConfig().SUPPORT_URL}`}>Help</a>
  </>
);const MOBILE_VIEW_HEADER_STYLES = `
  #root .indigo-mobile-header {
    align-items: center;
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
    display: flex;
    justify-content: space-between;
    height: 70px;
    padding: 10px 20px;
    position: relative;
    width: 100%;
    z-index: 1000;
  }

  #root .indigo-mobile-menu-toggle {
    align-items: center;
    background: transparent;
    border: 0;
    color: var(--pgn-color-primary-base, #ee7658);
    cursor: pointer;
    display: inline-flex;
    height: 44px;
    justify-content: center;
    padding: 0;
    position: relative;
    width: 28px;
  }

  #root .indigo-mobile-menu-toggle span,
  #root .indigo-mobile-menu-toggle::before,
  #root .indigo-mobile-menu-toggle::after {
    background: currentColor;
    content: "";
    display: block;
    height: 4px;
    position: absolute;
    transition: opacity 160ms ease, transform 160ms ease;
    width: 28px;
  }

  #root .indigo-mobile-menu-toggle span {
    position: relative;
  }

  #root .indigo-mobile-menu-toggle::before {
    transform: translateY(-9px);
  }

  #root .indigo-mobile-menu-toggle::after {
    transform: translateY(9px);
  }

  #root .indigo-mobile-menu-toggle.is-open span {
    opacity: 0;
  }

  #root .indigo-mobile-menu-toggle.is-open::before {
    transform: rotate(45deg);
  }

  #root .indigo-mobile-menu-toggle.is-open::after {
    transform: rotate(-45deg);
  }

  #root .indigo-mobile-menu {
    background: #fff;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.16);
    left: 0;
    list-style: none;
    margin: 0;
    padding: 10px 20px;
    position: absolute;
    right: 0;
    top: 100%;
  }

  #root .indigo-mobile-menu a {
    color: var(--pgn-color-primary-base, #ee7658);
    display: block;
    padding: 10px 0;
    font-size: 16px;
    font-weight: 400;
    text-decoration: none;
  }

  #root .indigo-mobile-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  #root .indigo-mobile-menu-section + .indigo-mobile-menu-section {
    border-top: 1px solid #e7e7e7;
    margin-top: 8px;
    padding-top: 8px;
  }

  #root .indigo-mobile-header .logo {
    display: block;
    height: auto;
    top: 0;
  }

  #root .indigo-mobile-header .logo-image {
    display: block;
    height: 54px !important;
  }
`;const MobileViewHeader = ({
  mainMenu = [],
  secondaryMenu = [],
  loggedIn = false,
  userMenu = [],
  loggedOutItems = [],
}) => {
  const config = getConfig();
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "alt text for the mobile view header logo",
    },
  };

  console.log("MobileViewHeader props", { mainMenu, secondaryMenu, userMenu, loggedOutItems });

  const BASE_URL = config.LMS_BASE_URL;
  const logoUrl = `${BASE_URL}/theming/asset/images/logo.svg`;
  const fallbackLogoUrl = `${BASE_URL}/static/indigo/images/logo.svg`;
  const menuItems = [...mainMenu, ...secondaryMenu];
  const accountItems = loggedIn ? userMenu : loggedOutItems;
  const normalizeMenuItem = (item) => ({
    href: item.href,
    label: item.content || item.label,
  });

  return (
    <header className="site-header-mobile indigo-mobile-header">
      <style>{MOBILE_VIEW_HEADER_STYLES}</style>
      <button
        type="button"
        className={`indigo-mobile-menu-toggle${isMenuOpen ? " is-open" : ""}`}
        aria-label="Meniu"
        aria-expanded={isMenuOpen}
        aria-controls="indigo-mobile-menu"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        <span aria-hidden="true" />
      </button>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img
          className="logo-image"
          src={logoUrl}
          alt="Panoul de control edX, Inc."
          onError={(e) => {
            if (e.currentTarget.src !== fallbackLogoUrl) e.currentTarget.src = fallbackLogoUrl;
          }}
        />
      </a>
      {isMenuOpen && (
        <ul id="indigo-mobile-menu" className="indigo-mobile-menu">
          <li className="indigo-mobile-menu-section">
            <ul>
              {menuItems.map(normalizeMenuItem).map((item) => (
                <li key={`${item.href}-${item.label}`}>
                  <a href={item.href}>{item.label}</a>
                </li>
              ))}
            </ul>
          </li>
          <li className="indigo-mobile-menu-section">
            <ul>
              {accountItems.flatMap((group) => group.items || group).map(normalizeMenuItem).map((item) => (
                <li key={`${item.href}-${item.label}`}>
                  <a href={item.href}>{item.label}</a>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      )}
    </header>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;
  const logoUrl = `${BASE_URL}/theming/asset/images/logo.svg`;
  const fallbackLogoUrl = `${BASE_URL}/static/indigo/images/logo.svg`;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img
          className="logo-image"
          src={logoUrl}
          alt="Panoul de control edX, Inc."
          onError={(e) => {
            if (e.currentTarget.src !== fallbackLogoUrl) e.currentTarget.src = fallbackLogoUrl;
          }}
        />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt="Open edX" />
      </a>
    </>
  );
};
const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookie = 'selected-paragon-theme-variant';
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getCookie(themeCookie) === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    window.localStorage.setItem(themeCookie, theme);
    setTimeout(() => {
      setCookie(themeCookie, theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  useEffect(() => {
    if (!getCookie(themeCookie) || getCookie(themeCookie) === 'undefined') {
      return;
    }
    if (getCookie(themeCookie) !== window.localStorage.getItem(themeCookie)) {
      window.localStorage.setItem(themeCookie, getCookie(themeCookie));
      window.location.reload();
    }
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getCookie(themeCookie) === "dark"}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"
            } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};const UsernameWidget = () => {
  const username = getAuthenticatedUser()?.username;

  if (!username) {
    return <div />;
  }

  return (
    <div className="username-dropdown">
      {username}
    </div>
  );
};const LearningUsernameWidget = () => {
  React.useEffect(() => {
    const makeLearningUserToggleIconOnly = () => {
      const toggle = document.querySelector('#root .learning-header .user-dropdown .dropdown-toggle');
      if (!toggle) {
        return;
      }

      const usernameLabel = toggle.querySelector('.d-none.d-md-inline');
      if (usernameLabel) {
        usernameLabel.style.display = 'none';
      }

      const iconWrapper = toggle.querySelector('.d-md-none');
      if (iconWrapper) {
        iconWrapper.classList.remove('d-md-none');
      }
    };

    const onUserMenuInteraction = (event) => {
      const trigger = event.target?.closest('.learning-header .user-dropdown .dropdown-toggle');
      if (!trigger) {
        return;
      }
      window.setTimeout(makeLearningUserToggleIconOnly, 0);
    };

    makeLearningUserToggleIconOnly();
    document.addEventListener('click', onUserMenuInteraction, true);
    document.addEventListener('keyup', onUserMenuInteraction, true);

    return () => {
      document.removeEventListener('click', onUserMenuInteraction, true);
      document.removeEventListener('keyup', onUserMenuInteraction, true);
    };
  }, []);

  return null;
};

    const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
    addPlugins(config, 'org.openedx.frontend.layout.header_desktop_secondary_menu.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.studio_header_search_button_slot.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    if (process.env.APP_ID == 'admin-console') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authn') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authoring') {


      const { CourseOutlineSidebar, UnitPageSidebar, SidebarToggleWrapper, CourseHeaderButton, UnitActionsButton, SubSectionAnalyticsButton } = await import("@openedx/frontend-plugin-aspects");


      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_sidebar.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'outline-sidebar',
                    priority: 1,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseOutlineSidebar,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_sidebar.v1', [
            {
                op: PLUGIN_OPERATIONS.Wrap,
                widgetId: 'default_contents',
                wrapper: SidebarToggleWrapper,
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'course-unit-sidebar',
                    priority: 1,
                    type: DIRECT_PLUGIN,
                    RenderWidget: UnitPageSidebar,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
                op: PLUGIN_OPERATIONS.Wrap,
                widgetId: 'default_contents',
                wrapper: SidebarToggleWrapper,
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'unit-header-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseHeaderButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'outline-header-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseHeaderButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_unit_card_extra_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'units-action-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: UnitActionsButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_subsection_card_extra_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'units-action-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: SubSectionAnalyticsButton,
                },
            }]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'account') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'communications') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.learning_header_actions.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'learning_header_actions_single_tray',
                        type: DIRECT_PLUGIN,
                        RenderWidget: LearningHeaderActionsSingleTray,
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.learning_header_actions.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'learning_header_actions_single_tray',
                        type: DIRECT_PLUGIN,
                        RenderWidget: LearningHeaderActionsSingleTray,
                    },
                },
            ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'gradebook') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'theme_switch_button',
                type: DIRECT_PLUGIN,
                RenderWidget: ToggleThemeButton,
            },
        },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
                {
                    op: PLUGIN_OPERATIONS.Modify,
                    widgetId: 'default_contents',
                    fn: (widget) => {
                        widget.content = widget.content || {};
                        widget.content.menu = [
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/dashboard`,
                                content: 'Cursuri',
                            },
                            {
                                type: 'item',
                                href: `${getConfig().LMS_BASE_URL}/courses`,
                                content: 'Descoperă cursuri noi',
                            },
                        ];

                        return widget;
                    },
                },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'chestionarprofesor') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const footerPluginsToInsert =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => plugin.op === 'insert');

  if (footerPluginsToInsert.length > 2) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'indigo_footer';
      }
      return true;
    });
  }
}
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;